<template>
	<article
		class="d-event-page"
		:class="{
			'd-event-page--no-footer-gap': !hasFooterGap,
		}"
	>
		<PageHeader :intro="intro" :details="{ ...details, time: null }">
			<ul
				v-if="details.location || details.time || details.price"
				class="
					flex
					<1024:flex-col
					gap-x-sm/h gap-y-4xs/v
					mt-xs/v
					text-label
					>=1024:text-body
				"
			>
				<li
					v-if="details.location && details.location.address"
					class="inline-flex items-center"
				>
					<SvgIconLocation
						class="
							w-16
							>=1024:w-21
							h-20
							>=1024:h-24
							mr-8
							>=1024:mr-12
						"
					/>
					<span
						v-text="
							[details.location.name, details.location.address]
								.filter(Boolean)
								.join(', ')
						"
					></span>
				</li>
				<li v-if="details.time" class="inline-flex items-center">
					<SvgIconClock
						class="
							w-16
							>=1024:w-21
							h-20
							>=1024:h-16
							mr-8
							>=1024:mr-12
						"
					/>
					<span v-text="details.time"></span>
				</li>
				<li v-if="details.price" class="inline-flex items-center">
					<SvgIconMoney
						class="
							w-16
							>=1024:w-21
							h-20
							>=1024:h-24
							mr-8
							>=1024:mr-12
						"
					/>
					<span v-text="details.price"></span>
				</li>
			</ul>

			<!-- As we overwrite the default slot, we add this as well -->
			<AreaReferences
				v-if="details.areas"
				class=">=1024:hidden mt-2xs/v"
				:items="details.areas"
				invert
				use-with-links
			/>
		</PageHeader>

		<LongReadController
			id="long-read-controller"
			:key="$route.path"
			v-slot="longReadController"
		>
			<PageProgressBar>
				<!-- TOC -->
				<PageProgressBarToc
					v-if="showToc"
					v-bind="longReadController"
					:placeholder="toc.title"
					:toc-to-top="toc.toTheTop"
				/>

				<!-- Title -->
				<span
					v-else-if="intro.title"
					class="pl-layout-margin py-12 text-input"
					v-html="intro.title"
				></span>

				<!-- Links -->
				<LinkDropdown
					v-if="links"
					class="ml-auto <1024:hidden min-w-280 mr-layout-margin"
					:links="links.items"
					:label="links.label"
					:description="links.description"
				/>
			</PageProgressBar>

			<!-- Links -->
			<LinkDropdown
				v-if="links"
				class="
					w-fit
					min-w-280
					mx-auto
					mt-16
					<768:w-full
					>=1024:hidden
					px-layout-margin
				"
				:links="links.items"
				:label="links.label"
				:description="links.description"
				all-red
			/>

			<div
				class="
					grid grid-cols-12
					items-start
					gap-y-2xl/v
					px-layout-margin
					mt-2xl/v
				"
			>
				<aside class="col-span-full >=1024:col-span-3">
					<AsideContactPersons
						v-if="contactPersons && contactPersons.length"
						:contact-persons="contactPersons"
					/>
				</aside>

				<div
					class="
						col-span-full
						>=1024:col-start-5 >=1024:col-span-8
						grid grid-cols-12
						>=1024:grid-cols-8
						items-start
						gap-y-2xl/v
					"
				>
					<p
						v-if="intro.teaser"
						class="
							col-span-full
							font-light
							text-sub-teaser text-red
						"
						v-text="intro.teaser"
					></p>

					<BlockList
						v-if="blocks && blocks.length"
						:block-list="blocks"
						class="col-span-full"
					/>
				</div>
			</div>
		</LongReadController>
	</article>
</template>

<script>
import PageHeader from '~/components/main/PageHeader.vue';
import AreaReferences from '~/components/main/AreaReferences';
import PageProgressBar from '~/components/main/PageProgressBar';
import { LongReadController } from '~/components/shared/LongRead';
import PageProgressBarToc from '~/components/main/PageProgressBarToc';
import LinkDropdown from '~/components/main/LinkDropdown';
import AsideContactPersons from '~/components/main/AsideContactPersons';
import BlockList from '~/components/main/BlockList';
import SvgIconLocation from '~/assets/svgs/icon-location.svg?inline';
import SvgIconClock from '~/assets/svgs/icon-clock.svg?inline';
import SvgIconMoney from '~/assets/svgs/icon-money.svg?inline';

export default {
	name: 'MainEventPage',

	components: {
		PageHeader,
		AreaReferences,
		PageProgressBar,
		LongReadController,
		PageProgressBarToc,
		LinkDropdown,
		AsideContactPersons,
		BlockList,
		SvgIconLocation,
		SvgIconClock,
		SvgIconMoney,
	},

	inheritAttrs: false,

	props: {
		intro: Object,
		details: {
			type: Object,
			default: () => ({}),
		},
		showToc: Boolean,
		contactPersons: {
			type: Array,
			default: () => [],
		},
		blocks: {
			type: Array,
			default: () => [],
		},
		toc: {
			type: Object,
			required: false,
		},
	},

	computed: {
		links() {
			return this.details?.links?.items?.length && this.details.links;
		},

		hasFooterGap() {
			const lastBlock = [...this.blocks].pop();
			if (
				lastBlock &&
				[
					'relatedContent',
					'contactPersons',
					'contactPersonExternal',
					'experts',
				].includes(lastBlock.alias)
			) {
				return false;
			}
			return true;
		},
	},
};
</script>
